import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn } from '@/Components/motion';
import { Link } from 'react-router-dom';
import { Separator } from '@/Components/ui/separator';
import { siteConfig } from '@/config/site';

const sections = [
    {
        title: 'Agreement Overview',
        content:
            `This Client Agreement ("Agreement") is entered into between you ("Client") and ${siteConfig.brand.name} ("Company"). By registering for an account, purchasing an evaluation, or using any of our services, you acknowledge that you have read, understood, and agree to be bound by the terms of this Agreement. This Agreement, together with our Terms and Conditions, Privacy Policy, and Risk Disclosure, constitutes the entire agreement between you and ${siteConfig.brand.name} regarding the use of our services.`,
    },
    {
        title: 'Services Description',
        content:
            `${siteConfig.brand.name} provides simulated trading evaluation programs, funded trading accounts, educational resources, and related tools and services. The Company reserves the right to modify, suspend, or discontinue any service at any time with or without notice. Services are provided on an "as is" and "as available" basis. The Company does not guarantee uninterrupted access to the platform or services. Specific service terms, including account sizes, profit targets, drawdown limits, and profit splits, are as described on the ${siteConfig.brand.name} website and may be updated from time to time.`,
    },
    {
        title: 'Client Obligations',
        content:
            `As a client of ${siteConfig.brand.name}, you agree to provide accurate and truthful information during registration and verification, comply with all trading rules and guidelines as published on our platform, maintain the security of your account credentials, not share, sell, or transfer your account to any third party, trade solely with your own skill and judgment (unless EAs are permitted), and promptly notify ${siteConfig.brand.name} of any unauthorized access to your account or any other security breach.`,
    },
    {
        title: 'Risk Acknowledgment',
        content:
            `You acknowledge and agree that trading in financial markets involves significant risk, simulated trading results do not guarantee future performance in live markets, you have read and understood the Risk Disclosure document, you are trading with capital you can afford to lose, ${siteConfig.brand.name} is not responsible for any losses incurred during trading, and past performance is not indicative of future results. You confirm that you are making your own independent trading decisions and that ${siteConfig.brand.name} does not provide financial advice.`,
    },
    {
        title: 'Account Termination',
        content:
            `Either party may terminate this Agreement at any time. ${siteConfig.brand.name} may terminate or suspend your account immediately, without prior notice or liability, for any reason, including if you breach any provision of this Agreement or violate our trading rules. Upon termination, your right to use our services will immediately cease. Any outstanding obligations, including payment obligations, shall survive termination. ${siteConfig.brand.name} reserves the right to withhold payouts if there is a suspected violation of our rules pending investigation.`,
    },
    {
        title: 'Dispute Resolution',
        content:
            'In the event of a dispute arising out of or relating to this Agreement, the parties agree to first attempt to resolve the matter through good-faith negotiations. If the dispute cannot be resolved through negotiation within thirty (30) days, it shall be submitted to binding arbitration in accordance with the rules of the applicable arbitration authority. The arbitration shall be conducted in the English language. Each party shall bear its own costs associated with the arbitration, unless the arbitrator determines otherwise. The decision of the arbitrator shall be final and binding on both parties.',
    },
    {
        title: 'Contact',
        content:
            'If you have any questions or concerns regarding this Client Agreement, please contact us at info@teffunded.com. You may also reach out through the contact page on our website. We are committed to addressing your inquiries in a timely and transparent manner.',
    },
];

export default function ClientAgreement() {
    return (
        <AppLayout title={`Client Agreement - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <FadeIn>
                    <div className="text-center max-w-4xl mx-auto">
                        <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                            Legal
                        </p>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Client{' '}
                            <span className="text-gradient">Agreement</span>
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                            The agreement between you and {siteConfig.brand.name} governing
                            the use of our trading services and platform.
                        </p>
                    </div>
                </FadeIn>
            </Section>

            {/* Content */}
            <Section className="py-16 bg-muted/30 border-t border-border">
                <FadeIn>
                    <Card className="max-w-4xl mx-auto rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                        <CardContent className="p-8 md:p-12">
                            <p className="text-sm text-muted-foreground mb-10">
                                Last updated: March 2026
                            </p>

                            <div className="space-y-10">
                                {sections.map((section, index) => (
                                    <div key={section.title}>
                                        <h2 className="text-xl font-semibold text-foreground mb-3">
                                            {index + 1}. {section.title}
                                        </h2>
                                        <p className="text-muted-foreground leading-relaxed">
                                            {section.content}
                                        </p>
                                        {index < sections.length - 1 && (
                                            <Separator className="mt-10" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </FadeIn>
            </Section>

            {/* Bottom spacer */}
            <Section className="py-16 bg-background border-t border-border">
                <div className="text-center max-w-2xl mx-auto">
                    <p className="text-sm text-muted-foreground">
                        Questions about this agreement?{' '}
                        <Link
                            to="/contact"
                            className="text-primary hover:underline"
                        >
                            Contact us
                        </Link>
                    </p>
                </div>
            </Section>
        </AppLayout>
    );
}
